/**
 * Rendering a timestamp in a named timezone.
 *
 * The app has no date library — Intl only — and no shared formatter: every
 * site calls new Date(x).toLocaleString(), which renders in the VIEWER's
 * timezone. That is fine for "last seen 5 minutes ago" and wrong for a
 * report, where an irrigator in Alberta and a clerk in Ontario must read the
 * same period boundaries off the same document.
 *
 * So these take the zone explicitly. The report's zone is stored on the run,
 * not taken from the browser.
 */

const pad = (n: number) => String(n).padStart(2, '0')

function partsIn(date: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date)
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value)
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    // Intl renders midnight as hour 24 in some engines; normalise to 0 so a
    // period boundary reads "00:00" rather than "24:00".
    hour: get('hour') % 24,
    minute: get('minute'),
    second: get('second'),
  }
}

/** "2026-07-01 00:00" — the same shape the CSV and the PDF use. */
export function formatInTimezone(value: string | number | Date, timeZone: string): string {
  const date = toDate(value)
  if (!date) return ''
  const p = partsIn(date, timeZone)
  return `${p.year}-${pad(p.month)}-${pad(p.day)} ${pad(p.hour)}:${pad(p.minute)}`
}

/** "2026-07-01 12:00:04" — for generation stamps. */
export function formatInTimezoneWithSeconds(
  value: string | number | Date,
  timeZone: string
): string {
  const date = toDate(value)
  if (!date) return ''
  const p = partsIn(date, timeZone)
  return `${p.year}-${pad(p.month)}-${pad(p.day)} ${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`
}

/** "1 Jul 2026" — compact, for list rows. */
export function formatDateInTimezone(value: string | number | Date, timeZone: string): string {
  const date = toDate(value)
  if (!date) return ''
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

/** "July 2026" — the period label. */
export function formatMonthInTimezone(value: string | number | Date, timeZone: string): string {
  const date = toDate(value)
  if (!date) return ''
  return new Intl.DateTimeFormat('en-CA', { timeZone, month: 'long', year: 'numeric' }).format(date)
}

/**
 * A volume at the report's precision.
 *
 * Never re-rounds: the value arrives already rounded server-side to the
 * configured precision, and this only chooses how many digits to show. Showing
 * fewer than were billed would make the column look wrong against the CSV.
 */
export function formatVolume(value: number, decimalPlaces: number): string {
  return value.toLocaleString('en-CA', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  })
}

function toDate(value: string | number | Date): Date | null {
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value
  const date = new Date(value)
  return isNaN(date.getTime()) ? null : date
}
